import { useQueryClient } from '@tanstack/react-query';
import useKlydoMutation, { UseKlydoMutationOptions } from './useKlydoMutation';
import userApiClient, { User } from '../api/user';
import useAnalytics from './useAnalytics';

const useAcceptStudioContract = (
  userId: string,
  options: Omit<
    UseKlydoMutationOptions<User, void, unknown>,
    'onSuccess' | 'onSettled'
  > & {
    onSuccess?: (user: User) => void;
    onSettled?: (user: User | undefined, error: unknown) => void;
  } = {},
) => {
  const queryClient = useQueryClient();
  const { trackEvent } = useAnalytics();

  return useKlydoMutation<User, void, unknown>({
    // Spread first — otherwise the caller's onSuccess/onSettled would
    // replace the wrappers below, dropping the analytics event and the
    // user-cache update on every contract signing
    ...options,
    mutationFn: () => userApiClient.acceptStudioContract(),
    mutationKey: ['acceptContract', userId],
    onSuccess: (updatedUser) => {
      trackEvent({ type: 'studio_contract_signed' });
      queryClient.setQueryData(['user', userId], updatedUser);
      options.onSuccess?.(updatedUser);
    },
    onError: options.onError,
    onSettled: (user, error) => {
      options.onSettled?.(user, error);
    },
  });
};

export default useAcceptStudioContract;
